import { useEffect, useMemo, useState } from "react"

import { FormInstance } from "antd"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { userSelector } from "features/user/model/selectors/userSelectors"
import { fetchUserDataThunk, updateUserDataThunk } from "features/user/model/slice/userSlice"
import { familyMembersSelector } from "features/team/model/selectors/teamSelectors"
import { NOTIFICATION_TYPES, showNotification } from "features/common/notifications"

import { ProfileForm } from "./ProfileForm"
import { ProfileFormSkeleton } from "./ProfileFormSkeleton"

interface ProfileFormValues {
  name: string;
  email: string;
  // currentPassword?: string;
  // newPassword?: string;
  // confirmPassword?: string;
}

// Extended interface to match the actual data structure
interface ExtendedMember {
  id: string;
  name?: string;
  phone_or_email?: string;
  email?: string;
}

export const Account = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(userSelector)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const teamMembers = useAppSelector(familyMembersSelector)
  const currentMember = teamMembers.find((member) => member.id === String(user?.id)) as ExtendedMember | undefined

  const userData = useMemo(() => ({
    name: currentMember?.name || user.name || "",
    email: currentMember?.phone_or_email || currentMember?.email || user.email || "",
  }), [currentMember, user])


  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(fetchUserDataThunk()).unwrap()
      } catch (error) {
        console.error('Error loading user data:', error)
        showNotification({
          type: NOTIFICATION_TYPES.ERROR,
          title: 'Failed to load user data',
          message: 'Failed to load user data',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [dispatch])

  const handleSubmit = async (form: FormInstance<ProfileFormValues>, values: ProfileFormValues) => {
    try {
      setIsSaving(true)
      await dispatch(updateUserDataThunk({
        id: user.id,
        name: values.name,
      })).unwrap()
      await dispatch(fetchUserDataThunk()).unwrap()

      form.setFieldsValue({
        name: values.name,
      })
      form.resetFields(['name'])
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Profile updated successfully',
        message: 'Your profile has been updated successfully',
      })
    } catch (error) {
      console.error('Error updating profile:', error)
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Failed to update profile',
        message: 'Failed to update profile',
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading || !user) {
    return <ProfileFormSkeleton />
  }


  return (
    <ProfileForm
      loading={isSaving}
      initialValues={userData}
      onSubmit={handleSubmit}
    />
  )
}
